import { forwardRef, InputHTMLAttributes, KeyboardEvent, useEffect, useState } from 'react';
import { cx } from '@/utils';
import SvgClose from '@common/Icons/Close';
import SvgSearch from '@common/Icons/Search';
import { Spinner } from '@material-tailwind/react';

import styles from './styles.module.css';

type Props = {
  iconColor?: string;
  onChange?: (value: string) => void;
  inputSearchAux?: string;
  onSubmit?: () => void;
  onDelete?: () => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  containerClass?: string;
} & Omit<InputHTMLAttributes<any>, 'onChange'>;

const SearchInput = forwardRef<HTMLInputElement, Props>((props, ref) => {
  const { value, containerClass, ...propsToSend } = props;

  const [isTyping, setIsTyping] = useState(false);
  const [timerId, setTimerId] = useState<number | any>(null);

  const onChange = (value: string) => {
    setIsTyping(true);
    if (props.onChange) {
      props.onChange(value);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && props.onSubmit) {
      props.onSubmit();
    }
    // Forward all keyboard events to parent for additional handling
    if (props.onKeyDown) {
      props.onKeyDown(event);
    }
  };

  useEffect(() => {
    if (timerId) {
      clearTimeout(timerId);
    }

    const newTimerId = setTimeout(() => {
      if (props.onSubmit) {
        props.onSubmit();
        setIsTyping(false);
      }
    }, 800);

    setTimerId(newTimerId);

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [value, props.inputSearchAux]);

  return (
    <div style={{ height: 42 }} className={cx(styles.wrapper, props.containerClass)}>
      <input
        className={'body-text1'}
        type="text"
        value={value}
        {...propsToSend}
        onChange={({ target: { value } }) => onChange(value)}
        onKeyDown={handleKeyDown}
        ref={ref}
      />
      <div className={'flex flex-row items-center gap-[10px] py-0.5'}>
        {props.value && (
          <button className={styles.close} onClick={props.onDelete}>
            <SvgClose className="cursor-pointer text-tundora" />
          </button>
        )}

        {isTyping && <Spinner />}
        <div className="mr-2 cursor-pointer" onClick={props.onSubmit}>
          {isTyping ? null : (
            <SvgSearch
              width={24}
              height={24}
              className={cx('cursor-pointer', { 'fill-marine': !props.iconColor }, props.iconColor)}
            />
          )}
        </div>
      </div>
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput;
